import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Flag, Users, ClipboardList, Timer, ShieldAlert, CheckCircle2, XCircle, ArrowRightLeft } from "lucide-react";
import { api, errMsg, label, fmtDate } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useMatch } from "../../lib/useMatch";
import { MatchHeader, TacticalHeader } from "../../components/MatchHeader";
import { EventTimeline } from "../../components/EventTimeline";
import { PageHeader, Panel, Empty, Spinner, Avatar, IconBox } from "../../components/kit";
import { CommsFeed, TeamSheets } from "../referee/TacticalKit";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const PRE_LIVE = ["scheduled", "awaiting_team_sheets", "teams_submitted", "teams_approved", "officials_confirmed", "ready_for_kickoff", "first_half", "half_time", "second_half", "et_break", "et_first_half", "et_half_time", "et_second_half", "pen_break", "penalty_shootout", "suspended"];
const AR_SIGNALS = ["Offside observation", "Foul observation", "Ball out of play", "Goal-line check", "Substitution ready", "Request review"];
const FOURTH_SIGNALS = ["Substitution ready", "Technical area alert", "Equipment issue", "Medical staff called", "Added time board ready", "Request review"];
const ROLE_ICON = { ar1: Flag, ar2: Flag, fourth: ClipboardList, commissioner: ShieldAlert, assessor: Timer };
const ROLE_NAME = { referee: "Referee", ar1: "Assistant Referee 1", ar2: "Assistant Referee 2", fourth: "Fourth Official", commissioner: "Match Commissioner", assessor: "Referee Assessor", var: "Video Official" };
const roleName = (r) => ROLE_NAME[r] || label(r);

const SubRow = ({ p, dir }) => (
  <div className="flex min-w-0 items-center gap-2">
    <Avatar player={p} size={40} />
    <div className="min-w-0"><div className={`text-[10px] font-extrabold uppercase ${dir === "OUT" ? "text-[#F02030]" : "text-[#35865C]"}`}>{dir}</div><div className="truncate text-sm font-bold">#{p?.number} {p?.short_name}</div></div>
  </div>
);

function SubQueue({ m }) {
  const [list, setList] = useState([]);
  const load = () => api.get(`/matches/${m.id}/substitution-requests`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [m]); // eslint-disable-line
  const P = (id) => m.players[id];
  const pend = list.filter((r) => r.status === "pending");
  const decide = async (r, approve) => {
    try {
      await api.post(`/substitution-requests/${r.id}/process`, approve ? { approve: true } : { approve: false, reason: "Not eligible" });
      toast.success(approve ? "Verified — passed to the referee to validate" : "Request rejected");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title={`Substitution requests · ${pend.length} pending`} testid="fourth-sub-queue">
      {!list.length && <Empty text="No requests yet. Coaches' requests appear here instantly." />}
      <div className="space-y-2">{[...pend, ...list.filter((r) => r.status !== "pending")].map((r) => (
        <div key={r.id} className={`rounded-[16px] border p-3 ${r.status === "pending" ? "border-[#171B20]/20 bg-white shadow-sm" : "border-[#E7EAEE] bg-[#F7F8FA] opacity-80"}`} data-testid={`subreq-${r.status}`}>
          <div className="flex flex-wrap items-center gap-2">
            <SubRow p={P(r.player_out_id)} dir="OUT" /><ArrowRightLeft className="h-4 w-4 shrink-0 text-[#6C757D]" /><SubRow p={P(r.player_in_id)} dir="IN" />
            <span className="ml-auto shrink-0 text-[10px] font-extrabold uppercase text-[#6C757D]">{r.team_id === m.home.team_id ? m.home.short : m.away.short} · {label(r.status)}</span>
          </div>
          {r.status === "pending" && <div className="mt-3 grid grid-cols-2 gap-2">
            <Button className="h-12 rounded-[14px]" data-testid={`approve-sub-${r.id}`} onClick={() => decide(r, true)}><CheckCircle2 className="h-4 w-4" />Verify & pass to referee</Button>
            <Button variant="outline" className="h-12 rounded-[14px]" data-testid={`reject-sub-${r.id}`} onClick={() => decide(r, false)}><XCircle className="h-4 w-4" />Reject</Button>
          </div>}
        </div>))}</div>
    </Panel>
  );
}

function ObservationForm({ m, elapsed, kinds, title, testid }) {
  const [kind, setKind] = useState(kinds[0]);
  const [txt, setTxt] = useState("");
  const minute = Math.floor(elapsed / 60) + 1;
  const send = async () => {
    try { await api.post(`/matches/${m.id}/communications`, { kind, text: `${minute}' ${txt.trim()}` }); toast.success("Observation sent to the referee"); setTxt(""); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title={title} testid={testid}>
      <div className="mb-2 flex flex-wrap gap-1.5">{kinds.map((k) => (
        <button key={k} data-testid={`obs-kind-${k}`} onClick={() => setKind(k)} className={`min-h-[40px] rounded-[12px] px-3 text-xs font-bold ${kind === k ? "bg-[#171B20] text-white" : "border border-[#E7EAEE] bg-[#F7F8FA]"}`}>{label(k)}</button>
      ))}</div>
      <Textarea data-testid="obs-text" value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={`What did you observe? (recorded at ${minute}')`} className="rounded-xl" />
      <Button data-testid="obs-send-btn" className="mt-2 h-12 w-full rounded-[14px]" disabled={!txt.trim()} onClick={send}>Send observation · {minute}'</Button>
      <p className="mt-2 text-[11px] text-[#6C757D]">Observations are advisory and never become official decisions automatically.</p>
    </Panel>
  );
}

function AddedTimeBoard({ m }) {
  const send = async (n) => {
    try { await api.post(`/matches/${m.id}/communications`, { kind: "added_time", text: `Added time board: +${n}'` }); toast.success(`+${n}' proposed to the referee`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Added time board" testid="added-time-board">
      <div className="grid grid-cols-5 gap-2">{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <button key={n} data-testid={`board-added-${n}`} onClick={() => send(n)} className="h-12 rounded-[14px] border border-[#E7EAEE] bg-[#F7F8FA] font-mono text-lg font-extrabold transition-colors hover:bg-[#171B20] hover:text-white">+{n}</button>
      ))}</div>
      <p className="mt-2 text-[11px] text-[#6C757D]">Proposes added time to the referee, who confirms it on the official clock.</p>
    </Panel>
  );
}

function ReportForm({ m, type, fields, title, extra = {} }) {
  const [f, setF] = useState(Object.fromEntries(fields.map(([k]) => [k, ""])));
  return (
    <Panel title={title} testid={`${type}-report-form`}>
      <div className="grid gap-2 md:grid-cols-2">{fields.map(([k, l, num]) => (
        <label key={k} className="block"><span className="tro-label">{l}</span>{num ? <Input type="number" min={1} max={10} data-testid={`${type}-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} /> : <Textarea data-testid={`${type}-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}</label>))}</div>
      <Button className="mt-3" data-testid={`${type}-submit`} onClick={async () => { try { await api.post(`/matches/${m.id}/reports`, { type, content: { ...f, ...extra }, signed: true }); toast.success("Report submitted"); } catch (e) { toast.error(errMsg(e)); } }}>Submit report</Button>
    </Panel>
  );
}

function Checklist({ m, role, items: base }) {
  const cur = m.checklists?.[role]?.items || {};
  const [items, setItems] = useState(Object.fromEntries(base.map((k) => [k, !!cur[k]])));
  const [notes, setNotes] = useState(m.checklists?.[role]?.notes || "");
  return (
    <Panel title={`${roleName(role)} checklist`} testid={`${role}-checklist`}>
      <div className="grid gap-2 sm:grid-cols-2">{base.map((k) => <label key={k} className="flex min-h-[40px] items-center gap-2 text-sm"><Checkbox checked={items[k]} onCheckedChange={(v) => setItems({ ...items, [k]: !!v })} />{k}</label>)}</div>
      <Textarea className="mt-2" placeholder="Outstanding issues" value={notes} onChange={(e) => setNotes(e.target.value)} />
      <Button className="mt-2" data-testid={`${role}-checklist-save`} onClick={async () => { try { await api.put(`/matches/${m.id}/checklists/${role}`, { items, notes }); toast.success("Checklist saved"); } catch (e) { toast.error(errMsg(e)); } }}>Save checklist</Button>
    </Panel>
  );
}

function AssignmentCard({ m, roles, live }) {
  const Icon = ROLE_ICON[roles[0]] || Flag;
  return (
    <div className="tro-card flex items-center gap-3 p-3 md:p-4" data-testid="assignment-card">
      <IconBox icon={Icon} active />
      <div className="min-w-0 flex-1">
        <div className="tro-label">Your assignment</div>
        <div className="truncate text-base font-extrabold" data-testid="my-official-role">{roles.map(roleName).join(" · ") || "—"}</div>
        <div className="truncate text-xs text-[#6C757D]">{m.match_number} · {m.venue} · {fmtDate(m.kickoff)}</div>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase ${live ? "bg-[#35865C] text-white" : "bg-[#E7EAEE] text-[#343A40]"}`}>{live ? "Live" : label(m.status)}</span>
    </div>
  );
}

function Attendance({ m }) {
  const rows = Object.entries(m.officials || {}).filter(([, v]) => v);
  const conf = m.confirmations || {};
  return (
    <Panel title={`Official attendance · ${rows.filter(([k]) => conf[k]).length}/${rows.length} confirmed`} testid="official-attendance">
      <div className="space-y-1.5">{rows.map(([k]) => (
        <div key={k} className="flex items-center gap-2 rounded-[12px] bg-[#F7F8FA] px-3 py-2 text-sm" data-testid={`attendance-${k}`}>
          {conf[k] ? <CheckCircle2 className="h-4 w-4 text-[#35865C]" /> : <XCircle className="h-4 w-4 text-[#B38F00]" />}
          <b className="min-w-0 flex-1 truncate">{roleName(k)}</b>
          <span className="text-[10px] font-bold uppercase text-[#6C757D]">{conf[k] ? `Confirmed ${fmtDate(conf[k])}` : "Not confirmed"}</span>
        </div>
      ))}</div>
    </Panel>
  );
}

function CommissionerView({ m, room, roles }) {
  const { me } = useAuth();
  const done = !PRE_LIVE.includes(m.status);
  const live = !done && !PRE_LIVE.slice(0, 6).includes(m.status);
  const comm = roles.includes("commissioner");
  return (
    <div className="space-y-3 md:space-y-4" data-testid="commissioner-oversight">
      <div className="sticky top-16 z-30 -mx-1 px-1"><TacticalHeader match={m} elapsed={room.elapsed} conn={room.conn} queue={room.queue} /></div>
      <AssignmentCard m={m} roles={roles} live={live} />
      <div className="grid grid-cols-1 gap-3 md:gap-4 xl:grid-cols-2">
        <div className="min-w-0 space-y-3 md:space-y-4">
          {comm && <Checklist m={m} role="commissioner" items={["Venue safety certificate", "Medical team present", "Security briefing done", "Officials' attendance confirmed", "Dressing rooms ready", "Pitch markings correct"]} />}
          <Attendance m={m} />
        </div>
        <div className="min-w-0 space-y-3 md:space-y-4">
          {comm && <ObservationForm m={m} elapsed={room.elapsed} testid="commissioner-incident-form" title="Incident notes" kinds={["incident"]} />}
          {comm && <ReportForm m={m} type="commissioner" title="Commissioner report" fields={[["compliance", "Organisational compliance"], ["venue_conditions", "Venue conditions"], ["incidents", "Matchday incidents"], ["observations", "Administrative observations"]]} />}
          <Panel title="Officials communication" testid="officials-comms"><CommsFeed id={m.id} me={me} events={room.events} signals={["Venue issue", "Crowd safety alert", "Medical staff called", "Security alert"]} /></Panel>
        </div>
        <Panel title="Official events" testid="officials-timeline" className="self-start"><EventTimeline compact events={room.events} match={m} /></Panel>
      </div>
    </div>
  );
}

function Workspace({ id, focus }) {
  const { me } = useAuth();
  const [ping, setPing] = useState(0);
  const [sheets, setSheets] = useState(false);
  const room = useMatch(id, { onMessage: (msg) => { if (msg.type === "communication") setPing((x) => x + 1); } });
  const m = room.match;
  if (!m) return <Spinner />;
  const roles = m.my_official_roles || [];
  if (focus) return <CommissionerView m={m} room={room} roles={roles} />;
  const fourth = roles.includes("fourth");
  const ar = roles.some((r) => r.startsWith("ar"));
  const done = !PRE_LIVE.includes(m.status);
  const live = !done && !PRE_LIVE.slice(0, 6).includes(m.status);
  return (
    <div className="space-y-3 md:space-y-4" data-testid={fourth ? "fourth-operations" : "ar-live-match"}>
      <div className="sticky top-16 z-30"><TacticalHeader match={m} elapsed={room.elapsed} conn={room.conn} queue={room.queue} /></div>
      <AssignmentCard m={m} roles={roles} live={live} />
      <div className="grid grid-cols-1 gap-3 md:gap-4 xl:grid-cols-2">
        {fourth && <div className="min-w-0 space-y-3 md:space-y-4">
          <SubQueue m={m} />
          <Panel title="Player verification" testid="player-verification">
            <p className="mb-3 text-sm text-[#6C757D]">Check numbers, photos and eligibility against both official team sheets.</p>
            <Button variant="outline" className="h-12 w-full rounded-[14px]" data-testid="open-team-sheets-btn" onClick={() => setSheets(true)}><Users className="h-4 w-4" />Team sheets & eligibility</Button>
          </Panel>
          <AddedTimeBoard m={m} />
        </div>}
        <div className="min-w-0 space-y-3 md:space-y-4">
          <Panel title={ar ? "Quick signals & communication" : "Officials communication"} testid="officials-comms">
            <CommsFeed id={m.id} me={me} ping={ping} events={room.events} signals={fourth ? FOURTH_SIGNALS : AR_SIGNALS} />
          </Panel>
          <ObservationForm m={m} elapsed={room.elapsed} testid={fourth ? "technical-area-form" : "observation-form"} title={fourth ? "Technical-area incident" : "Incident observation"} kinds={fourth ? ["technical_area_incident", "incident"] : ["offside_observation", "incident"]} />
        </div>
        {fourth && <Checklist m={m} role="fourth" items={["Substitution boards checked", "Player lists verified", "Technical areas marked", "Equipment checked"]} />}
        {roles.includes("assessor") && <ReportForm m={m} type="assessment" title="Referee assessment" extra={{ referee_id: m.officials.referee }} fields={[["overall_score", "Overall score (1-10)", true], ["performance", "Performance observations"], ["decisions", "Decision-review notes"], ["development", "Development feedback"]]} />}
        {ar && done && <ReportForm m={m} type="assistant" title="Assistant referee post-match notes" fields={[["offside_observations", "Offside observations"], ["incidents", "Incident report"], ["notes", "Post-match notes"]]} />}
        <Panel title="Official events" testid="officials-timeline" className="self-start"><EventTimeline compact events={room.events} match={m} /></Panel>
      </div>
      <TeamSheets open={sheets} onClose={() => setSheets(false)} match={m} reload={room.load} canApprove={false} />
    </div>
  );
}

export function OfficialsWorkspace({ commissioner }) {
  const { me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data.filter((m) => Object.values(m.officials || {}).includes(me.user.id)).sort((a, b) => ["validated", "archived"].includes(a.status) - ["validated", "archived"].includes(b.status)))); }, [me.user.id]);
  if (!list) return <Spinner />;
  const id = sp.get("match") || list.find((m) => PRE_LIVE.slice(6).includes(m.status))?.id || list.find((m) => m.status !== "validated")?.id || list[0]?.id;
  const cur = list.find((m) => m.id === id);
  const fourth = Object.entries(cur?.officials || {}).some(([k, v]) => k === "fourth" && v === me.user.id);
  return (
    <div>
      <PageHeader title={commissioner ? "Match Oversight" : fourth ? "Match Operations" : "Live Match"} sub={commissioner ? "Venue readiness, attendance and commissioner reports" : "Your role-specific tools for the selected match"}>
        <select data-testid="officials-match-select" value={id || ""} onChange={(e) => setSp({ match: e.target.value })} className="h-11 w-full min-w-0 max-w-full rounded-xl border px-2 text-sm sm:w-auto sm:max-w-md">{list.map((m) => <option key={m.id} value={m.id}>{m.match_number} · {m.home.name} v {m.away.name}</option>)}</select>
      </PageHeader>
      {id ? <Workspace key={id} id={id} focus={commissioner} /> : <Empty text="No appointments." />}
    </div>
  );
}

export const CommissionerChecklists = () => <OfficialsWorkspace commissioner />;
